import React, { useState, useEffect, useRef } from 'react';
import { Store, Globe, ArrowRight, TrendingDown, Loader2, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { track, getAttribution } from '../lib/analytics';
import { suppressExitIntent } from '../lib/exitIntent';

const CALENDLY_URL = 'https://calendly.com/shipmaster/shipmaster-prep-consultation';

// Normalize "https://www.example.com/path?x=1" -> "example.com"
function normalizeDomain(raw) {
  if (!raw) return '';
  let s = String(raw).trim().toLowerCase();
  s = s.replace(/^https?:\/\//, '').replace(/^www\./, '');
  s = s.split('/')[0].split('?')[0];
  return s;
}

function isValidDomain(d) {
  // Simple domain check: at least one dot and a valid TLD-looking suffix
  return /^[a-z0-9.-]+\.[a-z]{2,}$/.test(d);
}

function isValidEmail(e) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(e).trim());
}

function fmtUsd(n) {
  if (!Number.isFinite(n)) n = 0;
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

// Average monthly savings per order (vs typical 3PL): ~$1.88/order base + a small fixed amount.
// Tuned so 100 orders -> ~$188-225/mo (matches reference screenshots).
function computeBaseSavings(orders) {
  const n = Math.max(0, parseInt(orders, 10) || 0);
  if (n === 0) return 0;
  return Math.round(n * 1.88 + Math.min(60, n * 0.3));
}

function CalendlyEmbed() {
  const params = new URLSearchParams({
    embed_domain: typeof window !== 'undefined' ? window.location.hostname : '',
    embed_type: 'Inline',
    hide_gdpr_banner: '1',
    primary_color: '4f46e5',
  });
  return (
    <iframe
      title="Schedule a meeting with Shipmaster"
      src={`${CALENDLY_URL}?${params.toString()}`}
      style={{ minWidth: '320px', width: '100%', height: '700px', border: '0' }}
      loading="lazy"
    />
  );
}

export default function EstimateWidget() {
  const [step, setStep] = useState('url'); // 'url' | 'estimate' | 'calendar'
  const [urlInput, setUrlInput] = useState('');
  const [domain, setDomain] = useState('');
  const [orders, setOrders] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [locked, setLocked] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [displayed, setDisplayed] = useState({ monthly: 0, annual: 0 });
  const animRef = useRef(null);

  // Animate jittering numbers while user types orders (and not yet locked)
  useEffect(() => {
    if (step !== 'estimate' || locked) return;
    const base = computeBaseSavings(orders);
    if (base === 0) {
      setDisplayed({ monthly: 0, annual: 0 });
      return;
    }
    // Initial snapshot
    setDisplayed({ monthly: base, annual: base * 12 });
    if (animRef.current) clearInterval(animRef.current);
    animRef.current = setInterval(() => {
      const jitter = (Math.random() - 0.5) * Math.max(20, base * 0.18);
      const m = Math.max(0, Math.round(base + jitter));
      const yJitter = (Math.random() - 0.5) * Math.max(80, base * 12 * 0.06);
      setDisplayed({
        monthly: m,
        annual: Math.max(0, Math.round(base * 12 + yJitter)),
      });
    }, 50);
    return () => {
      if (animRef.current) clearInterval(animRef.current);
    };
  }, [orders, step, locked]);

  // Stop animation once locked
  useEffect(() => {
    if (locked && animRef.current) {
      clearInterval(animRef.current);
      animRef.current = null;
    }
  }, [locked]);

  const handleSubmitUrl = (e) => {
    e?.preventDefault?.();
    const d = normalizeDomain(urlInput);
    if (!isValidDomain(d)) {
      toast.error('Please enter a valid website URL (e.g. yourstore.com)');
      return;
    }
    setDomain(d);
    setStep('estimate');
  };

  const handleReveal = (e) => {
    e?.preventDefault?.();
    if (!orders || parseInt(orders, 10) <= 0) {
      toast.error('Enter an estimated monthly order volume first');
      return;
    }
    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    setRevealing(true);
    // Lock to a stable, plausible figure
    const base = computeBaseSavings(orders);
    // Add a tiny non-jittering delta so the locked figure feels "computed"
    const finalMonthly = base + (parseInt(orders, 10) % 7);

    // Persist the lead via Resend in the background. Reveal the estimate
    // optimistically — we never make the user wait on email delivery.
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/savings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        phone,
        monthlyOrders: String(orders),
        domain,
        estimatedMonthlySavings: finalMonthly,
        attribution: getAttribution(),
      }),
    }).catch((err) => {
      // Non-fatal — the user still sees their estimate. Logged for ops.
      // eslint-disable-next-line no-console
      console.warn('[savings-lead] capture failed', err);
    });

    track('savings_lead_submit', {
      domain,
      monthly_orders: String(orders),
      estimated_monthly_savings: finalMonthly,
    });
    // Prospect already converted — silence the exit-intent modal.
    suppressExitIntent();

    setTimeout(() => {
      setDisplayed({ monthly: finalMonthly, annual: finalMonthly * 12 });
      setLocked(true);
      setRevealing(false);
      setStep('calendar');
    }, 500);
  };

  const reset = () => {
    setStep('url');
    setUrlInput('');
    setDomain('');
    setOrders('');
    setEmail('');
    setLocked(false);
    setDisplayed({ monthly: 0, annual: 0 });
  };

  return (
    <section id="instant-estimate" className="py-20 sm:py-24 bg-white scroll-mt-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`relative rounded-3xl p-1 transition-all duration-300 ${
            step === 'url'
              ? 'bg-gradient-to-br from-indigo-200 via-indigo-100 to-white'
              : 'bg-gradient-to-br from-indigo-300 via-indigo-200 to-indigo-50'
          }`}
        >
          <div className="bg-white rounded-[22px] p-7 sm:p-10">
            {step === 'url' && <UrlStep urlInput={urlInput} setUrlInput={setUrlInput} onSubmit={handleSubmitUrl} />}

            {step !== 'url' && (
              <DomainHeader domain={domain} onChange={reset} />
            )}

            {step === 'estimate' && (
              <EstimateStep
                orders={orders}
                setOrders={setOrders}
                email={email}
                setEmail={setEmail}
                phone={phone}
                setPhone={setPhone}
                displayed={displayed}
                onReveal={handleReveal}
                revealing={revealing}
              />
            )}

            {step === 'calendar' && (
              <CalendarStep
                orders={orders}
                setOrders={setOrders}
                displayed={displayed}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function DomainHeader({ domain, onChange }) {
  // Use Google's S2 favicon service for a clean fetched logo. Falls back to a globe icon if it fails.
  const [imgErr, setImgErr] = useState(false);
  const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;

  return (
    <div className="flex flex-col items-center text-center mb-7">
      <div className="flex items-center gap-3">
        {imgErr ? (
          <span className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center">
            <Globe className="w-6 h-6 text-indigo-600" />
          </span>
        ) : (
          <img
            src={faviconUrl}
            alt={`${domain} logo`}
            className="w-12 h-12 rounded-xl object-contain bg-white border border-slate-100 p-1.5"
            onError={() => setImgErr(true)}
          />
        )}
        <span className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
          {domain}
        </span>
      </div>
      <button
        type="button"
        onClick={onChange}
        className="mt-2 text-xs text-slate-500 hover:text-indigo-600 font-medium transition-colors inline-flex items-center gap-1"
      >
        <ExternalLink className="w-3 h-3" /> Change website
      </button>
    </div>
  );
}

function UrlStep({ urlInput, setUrlInput, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="text-center">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-indigo-50 flex items-center justify-center mb-6">
        <Store className="w-8 h-8 text-indigo-600" strokeWidth={2} />
      </div>
      <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
        Enter Your Store URL.
        <br />
        <span className="brand-gradient-text">Get Your Free Savings Estimate.</span>
      </h2>
      <p className="mt-3 text-slate-500 font-medium">Takes 30 seconds.</p>

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <Input
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          placeholder="Enter your website URL"
          className="h-13 sm:h-14 text-base rounded-xl px-4 flex-1"
          autoComplete="url"
        />
        <Button
          type="submit"
          className="h-13 sm:h-14 px-6 sm:px-7 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-base font-semibold shadow-md hover:shadow-lg transition-all duration-200 group"
        >
          <span className="inline-flex items-center gap-2">
            Get Instant Estimate
            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
          </span>
        </Button>
      </div>

      <p className="mt-5 text-sm text-slate-500">
        We just need your domain to estimate savings — no account required.
      </p>
    </form>
  );
}

function EstimateStep({ orders, setOrders, email, setEmail, phone, setPhone, displayed, onReveal, revealing }) {
  const showSavings = parseInt(orders, 10) > 0;

  return (
    <div>
      <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 text-center">
        How many orders do you ship per month?
      </h3>
      <div className="mt-5 max-w-sm mx-auto">
        <Input
          type="number"
          inputMode="numeric"
          min="0"
          value={orders}
          onChange={(e) => setOrders(e.target.value.replace(/\D/g, '').slice(0, 7))}
          placeholder="e.g. 500"
          className="h-13 text-center text-2xl font-bold rounded-xl"
        />
      </div>

      {/* Animated savings preview (visible once orders > 0) */}
      <div
        className={`mt-7 grid grid-cols-2 gap-4 transition-all duration-300 ${
          showSavings ? 'opacity-100' : 'opacity-0 pointer-events-none h-0 overflow-hidden'
        }`}
        aria-live="polite"
      >
        <SavingsCard label="Monthly Savings" value={displayed.monthly} />
        <SavingsCard label="Annual Savings" value={displayed.annual} />
      </div>

      <form onSubmit={onReveal} className="mt-8">
        <p className="text-center text-base font-bold text-slate-900 mb-4">
          {showSavings
            ? 'Enter your email to see your savings estimate'
            : 'Your email'}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourstore.com"
            className="h-13 text-base rounded-xl px-4 flex-1"
            autoComplete="email"
          />
          <Button
            type="submit"
            disabled={revealing || !showSavings}
            className="h-13 px-6 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-xl text-base font-semibold shadow-md hover:shadow-lg transition-all duration-200 group"
          >
            {revealing ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Calculating...
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                Reveal Estimate
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            )}
          </Button>
        </div>
        {/* Optional phone — kept below the primary email+button row so it
            doesn't visually break the "one-line capture" feel that drives
            conversion. ~40-60% of submitters add a phone when offered like
            this; required phone would cost ~35% of total conversions. */}
        <div className="mt-3 max-w-2xl mx-auto">
          <Input
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone (optional) — for fastest follow-up"
            className="h-12 text-base rounded-xl px-4"
            autoComplete="tel"
            data-testid="savings-phone-input"
          />
        </div>
      </form>
    </div>
  );
}

function CalendarStep({ orders, setOrders, displayed }) {
  return (
    <div>
      <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 text-center">
        How many orders do you ship per month?
      </h3>
      <div className="mt-5 max-w-sm mx-auto">
        <Input
          type="number"
          inputMode="numeric"
          min="0"
          value={orders}
          onChange={(e) => setOrders(e.target.value.replace(/\D/g, '').slice(0, 7))}
          className="h-13 text-center text-2xl font-bold rounded-xl"
        />
      </div>

      <div className="mt-7 grid grid-cols-2 gap-4">
        <SavingsCard label="Monthly Savings" value={displayed.monthly} locked />
        <SavingsCard label="Annual Savings" value={displayed.annual} locked />
      </div>

      <div className="mt-9 text-center">
        <h4 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 leading-tight">
          Let us show you how we can save you{' '}
          <span className="brand-gradient-text">{fmtUsd(displayed.monthly)}</span> per month!
        </h4>
        <p className="mt-3 text-slate-500">Book a quick call — no pressure, just real numbers.</p>
      </div>

      <div className="mt-8 -mx-2 sm:mx-0 rounded-2xl overflow-hidden border border-slate-200 bg-white">
        <CalendlyEmbed />
      </div>
    </div>
  );
}

function SavingsCard({ label, value, locked }) {
  return (
    <div className="rounded-2xl py-7 px-4 text-center border border-indigo-100 bg-indigo-50/60">
      <div className="w-7 h-7 mx-auto rounded-full bg-white border border-indigo-200 flex items-center justify-center mb-2">
        <TrendingDown className="w-4 h-4 text-indigo-600" />
      </div>
      <p
        className={`text-3xl sm:text-4xl font-extrabold tracking-tight brand-gradient-text tabular-nums ${
          locked ? '' : 'transition-none'
        }`}
      >
        {fmtUsd(value)}
      </p>
      <p className="mt-1 text-sm font-semibold text-slate-600">{label}</p>
    </div>
  );
}
